import { useState, useEffect } from 'react';
import type { DashboardType, UserWidgetPreference } from '@/types/dashboard';
import { useDashboard } from '@/hooks/use-dashboard';
import { useCurrentUser } from '@/hooks/use-current-user';
import { DashboardTabs } from './dashboard-tabs';
import { Button } from '@/components/ui/button';
import { Settings2, LayoutDashboard } from 'lucide-react';
import { usePermissions } from '@/providers/PermissionsProvider';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';

// Helper component to render dashboard content - extracted to fix nested ternary (S3358)
function DashboardContent({
  isLoading,
  enabledWidgets,
  children,
  onCustomizeClick,
}: {
  isLoading: boolean;
  enabledWidgets: UserWidgetPreference[];
  children: (widgets: UserWidgetPreference[]) => React.ReactNode;
  onCustomizeClick?: () => void;
}) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Quick Actions Skeleton */}
        <div className="w-full bg-card border-b p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['qa-skeleton-1', 'qa-skeleton-2', 'qa-skeleton-3', 'qa-skeleton-4'].map((id) => (
              <div key={id} className="space-y-2">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            ))}
          </div>
        </div>

        {/* Widgets Grid Skeleton */}
        <div className="p-6 w-full">
          <div className="grid grid-cols-[repeat(2,1fr)] md:grid-cols-[repeat(4,1fr)] xl:grid-cols-[repeat(6,1fr)] gap-4 w-full">
            {[
              { id: 'ws-1', wide: true }, { id: 'ws-2', wide: false }, { id: 'ws-3', wide: false },
              { id: 'ws-4', wide: true }, { id: 'ws-5', wide: false }, { id: 'ws-6', wide: false },
              { id: 'ws-7', wide: true }, { id: 'ws-8', wide: false }, { id: 'ws-9', wide: false },
              { id: 'ws-10', wide: true }, { id: 'ws-11', wide: false }, { id: 'ws-12', wide: false },
            ].map((item) => (
              <div
                key={item.id}
                className={`col-span-2 ${item.wide ? 'md:col-span-4' : 'md:col-span-2'}`}
              >
                <div className="rounded-lg border bg-card p-6 space-y-4">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-48 w-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (enabledWidgets.length > 0) {
    return (
      <>
        {/* Quick Actions - Full Width, No Padding */}
        {enabledWidgets.some(w => w.widgetId === 'quick-actions-financial' || w.widgetId === 'quick-actions-asset-management') && (
          <div className="w-full">
            {children(enabledWidgets.filter(w => w.widgetId === 'quick-actions-financial' || w.widgetId === 'quick-actions-asset-management'))}
          </div>
        )}

        {/* Widgets Grid - With Padding */}
        <div className="p-6 w-full">
          <div className="grid grid-cols-[repeat(2,1fr)] md:grid-cols-[repeat(4,1fr)] xl:grid-cols-[repeat(6,1fr)] gap-4 w-full">
            {children(enabledWidgets.filter(w => w.widgetId !== 'quick-actions-financial' && w.widgetId !== 'quick-actions-asset-management'))}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="p-6">
      <div className="text-center py-12 border-2 border-dashed rounded-lg">
        <Settings2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
        <p className="text-muted-foreground">
          No widgets enabled for this dashboard
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={onCustomizeClick}
        >
          <Settings2 className="h-4 w-4 mr-2" />
          Customise Dashboard
        </Button>
      </div>
    </div>
  );
}

interface DashboardLayoutProps {
  readonly children: (widgets: UserWidgetPreference[]) => React.ReactNode;
  readonly onCustomizeClick?: () => void;
  readonly selectedDashboard?: DashboardType;
  readonly onDashboardChange?: (dashboard: DashboardType) => void;
}

export function DashboardLayout({
  children,
  onCustomizeClick,
  selectedDashboard: externalSelectedDashboard,
  onDashboardChange,
}: Readonly<DashboardLayoutProps>) {
  const [internalSelectedDashboard, setInternalSelectedDashboard] = useState<DashboardType>('asset-management');

  // Use external state if provided, otherwise use internal state
  const selectedDashboard = externalSelectedDashboard ?? internalSelectedDashboard;
  const setSelectedDashboard = (dashboard: DashboardType) => {
    if (onDashboardChange) {
      onDashboardChange(dashboard);
    } else {
      setInternalSelectedDashboard(dashboard);
    }
  };

  const { useUserPreferences, useDashboardPreferences } = useDashboard();
  const { data: userPrefs, isLoading: userPrefsLoading } = useUserPreferences();
  const { data: dashboardPrefs, isLoading: dashboardPrefsLoading } = useDashboardPreferences(selectedDashboard);
  const [initialized, setInitialized] = useState(false);
  const { hasPermission } = usePermissions();

  // Check which dashboards the user has access to
  const hasDashboardAccess = hasPermission('dashboard');
  const hasAuditAccess = hasPermission('audits-manager');
  const hasAccessToBothDashboards = hasDashboardAccess && hasAuditAccess;

  // Set initial dashboard from user preferences only once on mount (if not controlled externally)
  useEffect(() => {
    if (!externalSelectedDashboard && !initialized) {
      // Check localStorage first for persisted dashboard
      const persistedDashboard = localStorage.getItem('selectedDashboard') as DashboardType | null;

      if (persistedDashboard && hasAccessToBothDashboards) {
        setInternalSelectedDashboard(persistedDashboard);
      } else if (userPrefs?.selectedDashboardType) {
        setInternalSelectedDashboard(userPrefs.selectedDashboardType);
      } else if (hasAccessToBothDashboards) {
        // Default to asset-management if user has access to both
        setInternalSelectedDashboard('asset-management');
      } else if (hasDashboardAccess) {
        setInternalSelectedDashboard('financial');
      } else if (hasAuditAccess) {
        setInternalSelectedDashboard('asset-management');
      }
      setInitialized(true);
    }
  }, [userPrefs, externalSelectedDashboard, initialized, hasAccessToBothDashboards, hasDashboardAccess, hasAuditAccess]);

  // Persist dashboard selection to localStorage when it changes
  useEffect(() => {
    if (initialized && !externalSelectedDashboard && hasAccessToBothDashboards) {
      localStorage.setItem('selectedDashboard', selectedDashboard);
    }
  }, [selectedDashboard, initialized, externalSelectedDashboard, hasAccessToBothDashboards]);

  // Show all widgets regardless of isEnabled flag - user configuration state has been removed
  const enabledWidgets = (dashboardPrefs?.widgets ?? [])
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const isLoading = userPrefsLoading || dashboardPrefsLoading;

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Get ordinal suffix for day
  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Format date with ordinal suffix
  const getFormattedDate = () => {
    const now = new Date();
    const day = now.getDate();
    const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
    return format(now, `EEEE '${dayWithSuffix}' MMMM yyyy`);
  };

  // Get the current user's name from the useCurrentUser hook
  const { firstName: userName } = useCurrentUser();

  return (
    <div className="flex h-full">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with border and background */}
        <div className="flex-shrink-0 border-b bg-card">
          {/* Top section with greeting and tabs */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  {getGreeting()}, {userName}
                </h1>
              </div>
            </div>
            {/* Dashboard Tabs - Moved to the right */}
            {hasAccessToBothDashboards && (
              <DashboardTabs
                value={selectedDashboard}
                onChange={setSelectedDashboard}
                showTabs={hasAccessToBothDashboards}
              />
            )}
          </div>
        </div>

        {/* Scrollable Area - Contains Quick Actions and Widgets */}
        <div className="flex-1 overflow-y-auto">
          <DashboardContent
            isLoading={isLoading}
            enabledWidgets={enabledWidgets}
            onCustomizeClick={onCustomizeClick}
          >
            {children}
          </DashboardContent>
        </div>
      </div>
    </div>
  );
}
